/**
 * invoice-section.tsx — "Thông tin xuất hoá đơn" trên trang Thay đổi gói + form dùng lại ở hộp
 * thoại "Yêu cầu xuất hoá đơn" (lịch sử thanh toán).
 *
 * Bật "Xuất hoá đơn VAT" → chọn một mục trong danh bạ hoá đơn hoặc nhập mới (mặc định "Lưu vào
 * danh bạ"). Server kiểm tra + lưu danh bạ TRƯỚC khi tạo đơn trên cổng thanh toán SePay.
 */
import { useEffect, useRef, type ReactNode } from 'react'
import { Building2, FileText, Pencil, Plus, User } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Checkbox, Switch } from '@/components/ui/misc'
import { cn } from '@/lib/utils'
import type { BuyerType, InvoiceProfile } from '@/hooks/use-billing'
import {
  draftFromInfo, emptyDraft, profileLabel, type InvoiceChoice, type InvoiceDraft, type InvoiceErrors, type InvoiceField,
} from './invoice-info'

export function initialChoice(profiles: InvoiceProfile[] | undefined, email: string): InvoiceChoice {
  const def = profiles?.find((p) => p.isDefault) ?? profiles?.[0]
  return {
    // Tổ chức đã từng lưu thông tin hoá đơn → bật sẵn; lần đầu → tắt.
    enabled: !!def,
    profileId: def?.id ?? null,
    editing: false,
    draft: def ? draftFromInfo(def) : emptyDraft('company', email),
    save: true,
  }
}

export function InvoiceSection({
  choice, onChange, profiles, loadingProfiles, errors, disabled, defaultEmail, hideToggle, notReady,
}: {
  choice: InvoiceChoice
  onChange: (c: InvoiceChoice) => void
  profiles: InvoiceProfile[]
  loadingProfiles: boolean
  errors: InvoiceErrors
  disabled?: boolean
  defaultEmail: string
  /** Hộp thoại "Yêu cầu xuất hoá đơn": luôn bật, không hiện công tắc. */
  hideToggle?: boolean
  /** Hệ thống hoá đơn điện tử chưa sẵn sàng — báo khách hoá đơn sẽ được phát hành bổ sung, không để họ chờ vô thời hạn không rõ lý do. */
  notReady?: boolean
}) {
  const { t } = useTranslation('billing')
  const selected = profiles.find((p) => p.id === choice.profileId) ?? null
  const set = (patch: Partial<InvoiceChoice>) => onChange({ ...choice, ...patch })

  return (
    <div className={cn('space-y-4', !hideToggle && 'rounded-xl border p-4')}>
      {!hideToggle && <label className="flex cursor-pointer items-start justify-between gap-4">
        <span>
          <span className="flex items-center gap-1.5 text-sm font-medium"><FileText className="h-4 w-4 text-primary" /> {t('invoiceSection.toggleLabel')}</span>
          <span className="mt-0.5 block text-xs text-muted-foreground">
            {t('invoiceSection.toggleHint')}
          </span>
        </span>
        <Switch checked={choice.enabled} disabled={disabled} onCheckedChange={(v) => set({ enabled: v })} aria-label={t('invoiceSection.toggleLabel')} />
      </label>}

      {choice.enabled && notReady && (
        <p role="note" className="rounded-lg border border-warning/40 bg-warning/10 px-3 py-2 text-xs">
          {t('invoiceSection.notReady')}
        </p>
      )}

      {choice.enabled && (
        <div className="space-y-4">
          {loadingProfiles ? (
            <p className="text-sm text-muted-foreground">{t('invoiceSection.loadingProfiles')}</p>
          ) : profiles.length > 0 && (
            <div className="space-y-2">
              <p className="text-sm">{t('invoiceSection.chooseFromProfiles')}</p>
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                {profiles.map((p) => {
                  const on = p.id === choice.profileId
                  return (
                    <button
                      key={p.id}
                      type="button"
                      disabled={disabled}
                      onClick={() => set({ profileId: p.id, editing: false, draft: draftFromInfo(p) })}
                      className={cn(
                        'rounded-lg border px-3 py-2 text-left text-sm transition-colors',
                        on ? 'border-primary bg-primary/[0.04] shadow-[0_0_0_1px_hsl(var(--primary))]' : 'hover:border-primary/40',
                      )}
                    >
                      <span className="flex items-center gap-1.5 font-medium">
                        {p.buyerType === 'company' ? <Building2 className="h-3.5 w-3.5 text-muted-foreground" /> : <User className="h-3.5 w-3.5 text-muted-foreground" />}
                        <span className="truncate">{profileLabel(p)}</span>
                        {p.isDefault && <span className="shrink-0 rounded bg-muted px-1.5 text-xs font-normal text-muted-foreground">{t('invoiceSection.default')}</span>}
                      </span>
                      <span className="mt-0.5 block truncate text-xs text-muted-foreground">{p.emails.join(', ')}</span>
                    </button>
                  )
                })}
                <button
                  type="button"
                  disabled={disabled}
                  onClick={() => set({ profileId: null, editing: false, draft: emptyDraft(choice.draft.buyerType, defaultEmail), save: true })}
                  className={cn(
                    'flex items-center justify-center gap-1.5 rounded-lg border border-dashed px-3 py-2 text-sm text-muted-foreground transition-colors hover:border-primary/40 hover:text-foreground',
                    choice.profileId == null && 'border-primary text-primary',
                  )}
                >
                  <Plus className="h-4 w-4" /> {t('invoiceSection.enterNew')}
                </button>
              </div>
            </div>
          )}

          {selected && !choice.editing ? (
            <div className="rounded-lg bg-muted/40 p-3 text-sm">
              <div className="flex items-start justify-between gap-3">
                <dl className="grid min-w-0 grid-cols-[auto_1fr] gap-x-3 gap-y-1">
                  <Dt>{selected.buyerType === 'company' ? t('invoiceSection.unit') : t('invoiceSection.individualOrHousehold')}</Dt>
                  <Dd>{selected.buyerType === 'company' ? selected.companyName : selected.buyerName}</Dd>
                  {selected.buyerType === 'company' && selected.taxCode && (<><Dt>{t('invoiceSection.taxCode')}</Dt><Dd>{selected.taxCode}</Dd></>)}
                  {selected.buyerType !== 'company' && selected.nationalId && (<><Dt>{t('invoiceSection.nationalId')}</Dt><Dd>{selected.nationalId}</Dd></>)}
                  {selected.address && (<><Dt>{t('invoiceSection.address')}</Dt><Dd>{selected.address}</Dd></>)}
                  <Dt>{t('invoiceSection.email')}</Dt><Dd>{selected.emails.join(', ')}</Dd>
                </dl>
                <button type="button" disabled={disabled} onClick={() => set({ editing: true, save: true })}
                  className="flex shrink-0 items-center gap-1 text-xs text-primary hover:underline">
                  <Pencil className="h-3 w-3" /> {t('invoiceSection.edit')}
                </button>
              </div>
            </div>
          ) : (
            <>
              <InvoiceForm draft={choice.draft} onChange={(draft) => set({ draft })} errors={errors} disabled={disabled} />
              <label className="flex items-center gap-2 text-sm">
                <Checkbox checked={choice.save} disabled={disabled} onCheckedChange={(v) => set({ save: v === true })} />
                {choice.profileId ? t('invoiceSection.updateProfile') : t('invoiceSection.saveToProfiles')}
              </label>
            </>
          )}
        </div>
      )}
    </div>
  )
}

function Dt({ children }: { children: ReactNode }) {
  return <dt className="text-muted-foreground">{children}</dt>
}
function Dd({ children }: { children: ReactNode }) {
  return <dd className="min-w-0 break-words">{children}</dd>
}

/** Form nhập thông tin người mua. */
export function InvoiceForm({
  draft, onChange, errors, disabled,
}: {
  draft: InvoiceDraft
  onChange: (d: InvoiceDraft) => void
  errors: InvoiceErrors
  disabled?: boolean
}) {
  const { t } = useTranslation('billing')
  const company = draft.buyerType === 'company'
  const firstError = useRef<HTMLDivElement>(null)
  const errorKey = Object.keys(errors).join(',')
  useEffect(() => {
    if (errorKey) firstError.current?.querySelector<HTMLInputElement>('[aria-invalid="true"]')?.focus()
  }, [errorKey])

  const field = (k: InvoiceField, label: string, opts: { required?: boolean; placeholder?: string; wide?: boolean; inputMode?: 'numeric' | 'email' | 'tel' } = {}) => (
    <div className={cn('space-y-1', opts.wide && 'sm:col-span-2')}>
      <Label htmlFor={`inv-${k}`} className="text-xs">
        {label}{opts.required && <span className="text-destructive"> *</span>}
      </Label>
      <Input
        id={`inv-${k}`}
        value={draft[k]}
        disabled={disabled}
        inputMode={opts.inputMode}
        placeholder={opts.placeholder}
        aria-invalid={!!errors[k]}
        onChange={(e) => onChange({ ...draft, [k]: e.target.value })}
        className={cn(errors[k] && 'border-destructive focus-visible:ring-destructive')}
      />
      {errors[k] && <p className="text-xs text-destructive">{errors[k]}</p>}
    </div>
  )

  const setType = (bt: BuyerType) => onChange({ ...draft, buyerType: bt })

  return (
    <div ref={firstError} className="space-y-3">
      <div role="radiogroup" aria-label={t('invoiceSection.buyerTypeLabel')} className="inline-flex rounded-lg border p-0.5 text-sm">
        {([['company', t('invoiceSection.buyerTypeCompany'), Building2], ['individual', t('invoiceSection.buyerTypeIndividual'), User]] as const).map(([bt, label, Icon]) => (
          <button
            key={bt}
            type="button"
            role="radio"
            aria-checked={draft.buyerType === bt}
            disabled={disabled}
            onClick={() => setType(bt)}
            className={cn(
              'flex items-center gap-1.5 rounded-md px-3 py-1.5 transition-colors',
              draft.buyerType === bt ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground',
            )}
          >
            <Icon className="h-3.5 w-3.5" /> {label}
          </button>
        ))}
      </div>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
        {company ? (
          <>
            {field('taxCode', t('invoiceSection.taxCode'), { required: true, placeholder: t('invoiceSection.taxCodePlaceholder'), inputMode: 'numeric' })}
            {field('companyName', t('invoiceSection.unitName'), { required: true, placeholder: t('invoiceSection.unitNamePlaceholder') })}
            {field('address', t('invoiceSection.address'), { required: true, wide: true, placeholder: t('invoiceSection.addressPlaceholderCompany') })}
            {field('buyerName', t('invoiceSection.buyerName'), { placeholder: t('invoiceSection.optional') })}
          </>
        ) : (
          <>
            {field('buyerName', t('invoiceSection.buyerNameOrHousehold'), { required: true })}
            {field('nationalId', t('invoiceSection.nationalId'), { placeholder: t('invoiceSection.nationalIdPlaceholder'), inputMode: 'numeric' })}
            {field('address', t('invoiceSection.address'), { required: true, wide: true })}
          </>
        )}
        {field('emails', t('invoiceSection.invoiceEmails'), { required: true, placeholder: t('invoiceSection.invoiceEmailsPlaceholder'), inputMode: 'email' })}
        {field('phone', t('invoiceSection.phone'), { inputMode: 'tel', placeholder: t('invoiceSection.optional') })}
      </div>
    </div>
  )
}
